import type { Destination, Transport, TripConfig } from '../types';
import { addDays } from './travelState';
import { cityLabel, matchesCity } from './placeNames';
import { visitForDate } from '../../functions/src/travellerRoutePolicy.mjs';

type SavedTransport = Transport & { _dayDate?: string };
export function rentalOverlapsVisit(t: SavedTransport, destination: Destination): boolean {
  if (!t.isRental) return false;
  const start = (t.dep || t._dayDate || '').slice(0, 10);
  const end = (t.arr || t.dep || t._dayDate || '').slice(0, 10);
  return !!start && !!end && start <= destination.departureDate && end >= destination.arrivalDate;
}

/** Project stay transport without moving its stored day, identity or expense. */
export function transportDuringVisit(t: SavedTransport, visit: Destination, all: SavedTransport[]): boolean {
  if (t.isRental) return rentalOverlapsVisit(t, visit);
  const start = (t.dep || t._dayDate || '').slice(0, 10);
  const end = (t.arr || t.dep || t._dayDate || '').slice(0, 10);
  if (!start || !end || start < visit.arrivalDate || end > visit.departureDate || end < start) return false;
  const from = t.fromCity || cityLabel(t.from);
  const to = t.toCity || cityLabel(t.to);
  const fromBase = matchesCity(from, visit.name), toBase = matchesCity(to, visit.name);
  if (!fromBase && !toBase) return false;
  if (fromBase && toBase) return true;
  if (fromBase && start > visit.arrivalDate && end < visit.departureDate) return true;
  // ponytail: infer excursions from dates/routes; add an explicit purpose only
  // if ambiguous bookings need overrides. Incoming legs require return evidence
  // so a late (+1) arrival is not mistaken for a local excursion.
  return all.some(other => {
    if (other === t || other.isRental || (t.id && other.id === t.id)) return false;
    if (!matchesCity(other.fromCity || other.from, to) || !matchesCity(other.toCity || other.to, from)) return false;
    const out = fromBase ? t : other, back = fromBase ? other : t;
    return out.dep?.slice(0, 10) === start && back.arr?.slice(0, 10) === start
      && out.arr?.includes('T') && back.dep?.includes('T') && out.arr <= back.dep;
  });
}

export const SHARED_ITEM_FIELDS = ['attractions', 'eats', 'nightlife', 'shopping', 'notes'] as const;
export type SharedItemField = typeof SHARED_ITEM_FIELDS[number];
export type DestinationItem = { id: string; [key: string]: unknown };

type DayContent = Partial<Record<SharedItemField | 'travellers' | 'transports', unknown>>;
export function sharedDayChanges(updated: DayContent, previous?: DayContent): Record<string, unknown> {
  if (!previous) return {};
  return Object.fromEntries(([...SHARED_ITEM_FIELDS, 'travellers'] as const)
    .filter(field => JSON.stringify(updated[field]) !== JSON.stringify(previous[field]))
    .map(field => [field, updated[field] ?? (field === 'travellers' ? null : [])]));
}

/** The destination id used everywhere a destination needs a stable key (Day.destinationId,
 *  the schedules/{destId} doc, etc.) — destinations have no stored id, just a name. */
/** A destination's title: only the level that was picked ("Le Marais", "Tuscany"),
 *  never joined to its country, which has its own field. Lookups keep the full name. */
export const destTitle = (name: string) =>
  (name || '').split(',')[0].trim()
    // A state or province abbreviation left on the end by Google's raw
    // autocomplete text ("Daylesford VIC", saved before picking was enforced).
    // Two or three capitals after a real word: Bali, Goa and NYC are safe.
    .replace(/^(.*[a-z].*?)\s+[A-Z]{2,3}$/, '$1');

export const destSlug = (name: string) => name.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');

/**
 * A destination's identity: its stored id, or — for one saved before ids
 * existed — the key trip merges always used for it (where + when). The two
 * agree by construction, so an old destination and its first saved id are the
 * same destination, and once stored the id survives a rename or date move.
 */
import { destinationKey } from '../../functions/src/travellerRoutePolicy.mjs';
export { destinationKey } from '../../functions/src/travellerRoutePolicy.mjs';

/** Give every destination its id, deriving the missing ones. */
export function withDestinationIds<T extends { id?: string; name: string; arrivalDate: string }>(destinations: T[]): T[];
export function withDestinationIds<T extends { id?: string; name: string; arrivalDate: string }>(destinations: T[] | undefined): T[] | undefined;
export function withDestinationIds<T extends { id?: string; name: string; arrivalDate: string }>(destinations: T[] | undefined) {
  return destinations?.map(d => d.id ? d : { ...d, id: destinationKey(d) });
}

/**
 * Transfer days belong to the arriving stop; the final departure stays with the
 * last stop. An overnight transfer — leaving one stop on the 14th, arriving at
 * the next on the 15th — makes the 14th travel, not a gap: it stays with the
 * stop you leave from, which is where you spend that day.
 */
export function destinationForDate(destinations: Destination[], date: string): Destination | undefined {
  return visitForDate(destinations,date);
}

/**
 * Does an existing destination already account for a visit arriving on this
 * date? The itinerary importer asks this before adding each destination to an
 * existing trip, so anything it says yes to is silently not added.
 *
 * It used to answer with `arrival <= date && departure >= date` — departure
 * INCLUSIVE — and consecutive destinations always share a day: Rome departs on
 * the 14th, Florence arrives on the 14th. So Florence's arrival counted as
 * "covered by Rome", and importing an itinerary to extend a trip dropped every
 * destination that followed one already in it. Its bookings still landed on
 * the right days, under no destination at all.
 *
 * A stay owns [arrival, departure): the departure day belongs to wherever you
 * arrive next, which is the convention destinationForDate already uses. A
 * visit starting on the same day as an existing one is the same visit (that is
 * what keeps a re-import from duplicating, zero-night stopovers included), and
 * one starting inside an existing stay would conflict with it. A destination
 * with no dates can't account for anything.
 */
export function arrivalCovered(date: string, existing: Pick<Destination, 'arrivalDate' | 'departureDate'>[]): boolean {
  if (!date) return false;
  return existing.some(d => !!d.arrivalDate && (
    d.arrivalDate === date
    || (!!d.departureDate && d.arrivalDate <= date && date < d.departureDate)
  ));
}

export function destinationDates(config: Pick<TripConfig, 'startDate' | 'endDate' | 'destinations'>, date: string): string[] {
  const destination = destinationForDate(config.destinations ?? [], date);
  if (!destination || date < config.startDate || date > config.endDate) return [date];
  const result: string[] = [];
  const start = destination.arrivalDate > config.startDate ? destination.arrivalDate : config.startDate;
  const end = destination.departureDate < config.endDate ? destination.departureDate : config.endDate;
  for (let day = new Date(`${start}T00:00:00Z`); day.toISOString().slice(0, 10) <= end; day.setUTCDate(day.getUTCDate() + 1)) {
    const key = day.toISOString().slice(0, 10);
    if (destinationForDate(config.destinations, key) === destination) result.push(key);
  }
  return result.length ? result : [date];
}

/** Apply only this item's change, retaining unrelated items and existing metadata. */
export function changeDestinationItem(items: DestinationItem[], id: string, patch: Record<string, unknown> | null): DestinationItem[] {
  if (patch === null) return items.filter(item => item.id !== id);
  const existing = items.find(item => item.id === id);
  const item = { ...existing, ...patch, id };
  return existing ? items.map(value => value.id === id ? item : value) : [...items, item];
}
